import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const AnimatedBlock = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const ImagineSection = () => {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="imagine-section" className="py-24 md:py-32 bg-[#F5F0EB]">
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <AnimatedBlock>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#442F23] text-center mb-12">
            Immagina la tua vita tra 6&ndash;12 mesi
          </h2>
        </AnimatedBlock>

        <div className="space-y-6 text-center">
          <AnimatedBlock delay={0.1}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Ti alzi al mattino e sai esattamente chi sei.<br />
              Non perch&eacute; hai tutte le risposte&hellip;<br />
              ma perch&eacute; sei connessa a qualcosa di pi&ugrave; profondo.<br />
              <span className="font-medium text-[#AC8E60]">La tua anima.</span>
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.15}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Vivi le tue giornate con direzione. Con presenza.<br />
              Con una forza calma che non avevi mai sentito prima.<br />
              E dentro di te&hellip; c'&egrave; chiarezza.
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.2}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Non hai pi&ugrave; bisogno di nasconderti.<br />
              Non hai pi&ugrave; bisogno di adattarti per essere accettata.<br />
              Ti mostri per quella che sei, senza maschere:<br />
              <strong>vera, potente, luminosa.</strong>
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.25}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Hai lasciato andare strati, condizionamenti, versioni di te costruite per sopravvivere&hellip;<br />
              e hai finalmente ricordato chi sei davvero.
            </p>
            <p className="font-heading text-2xl text-[#AC8E60] font-semibold mt-4">
              Un Guerriero di Luce.
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.3}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              La tua mente &egrave; chiara. Il tuo corpo &egrave; radicato e al sicuro.<br />
              La tua energia &egrave; allineata.
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.35}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Non sei pi&ugrave; intrappolata in cicli infiniti di guarigione o confusione spirituale.<br />
              Ora ogni passo ha direzione. Ogni scelta &egrave; guidata.<br />
              Perch&eacute; ora sai: <strong>sei qui per una missione.</strong><br />
              E non la stai pi&ugrave; rimandando. La stai vivendo.
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.4}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Hai smesso di dubitare del tuo valore.<br />
              Hai smesso di sentirti &ldquo;troppo&rdquo; o &ldquo;non abbastanza&rdquo;.<br />
              Non ti abbandoni pi&ugrave; per compiacere gli altri.<br />
              Non ti blocchi pi&ugrave; per paura del prossimo passo.<br />
              <strong>Ti fidi.</strong> Ascolti la tua intuizione&hellip; e agisci.
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.45}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Le tue emozioni non sono pi&ugrave; qualcosa da controllare, ma messaggi da onorare.<br />
              I tuoi desideri non sono pi&ugrave; qualcosa da ridimensionare, ma direzione da seguire.<br />
              E per la prima volta&hellip; <strong>stai vivendo nella tua verit&agrave;.</strong>
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.5}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Hai smesso di circondarti da ambienti che ti limitano.<br />
              Hai scelto relazioni che ti espandono.<br />
              Persone che ti vedono davvero. Che parlano la tua stessa lingua.<br />
              Che non mettono in discussione la tua crescita&hellip; la celebrano.
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.55}>
            <p className="font-body text-base text-[#442F23]/80 leading-relaxed">
              Hai capito che la tua sensibilit&agrave; &egrave; un dono.<br />
              Che la tua profondit&agrave; &egrave; la tua forza.<br />
              Che la tua luce &egrave; ci&ograve; per cui sei qui.<br />
              <strong>E ora la incarni.</strong>
            </p>
          </AnimatedBlock>

          <AnimatedBlock delay={0.6}>
            <div className="mt-8 py-8 border-t border-[#AC8E60]/15">
              <p className="font-heading text-xl md:text-2xl text-[#442F23] leading-relaxed mb-4">
                Questa non &egrave; una visione lontana.<br />
                &Egrave; ci&ograve; che accade quando smetti di vivere a met&agrave;
                e scegli di attivarti davvero.
              </p>
              <p className="font-body text-base text-[#AC8E60] font-medium mb-2">
                <strong>Light Workers House</strong> non &egrave; solo un percorso.<br />
                &Egrave; uno spazio di trasformazione reale.
              </p>
              <p className="font-body text-sm text-[#442F23]/60 mt-6 italic">
                La tua missione ti sta gi&agrave; chiamando.<br />
                Tutto cambia nel momento in cui rispondi.<br />
                Prima dici s&igrave;&hellip; prima inizi a incarnare la tua luce.
              </p>
            </div>
          </AnimatedBlock>

          <AnimatedBlock delay={0.65}>
            <button
              onClick={() => scrollTo("pricing-section")}
              className="btn-primary text-base mt-4"
              data-testid="imagine-cta"
            >
              Voglio Iscrivermi
            </button>
          </AnimatedBlock>
        </div>
      </div>
    </section>
  );
};

export default ImagineSection;
